import { useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Colour family for the bubble, matching the control it describes.
 *
 * These are the SAME fills the matching hover states use, so hovering a control
 * gives one colour rather than two near-misses.
 *
 * The foreground is per-tone rather than always white, because a vivid yellow
 * cannot carry white text: `amber-500` against white is 2.15:1, less than half
 * the 4.5:1 AA minimum. Flipping to a dark foreground on that one tone buys the
 * brightest colour in the set AND the best contrast in it. Measured:
 *
 *   primary-600 #2f5aa3  white     6.75:1
 *   amber-500   #f59e0b  gray-900  8.26:1
 *   green-700   #047857  white     5.48:1   (emerald)
 *   red-600     #dc2626  white     4.83:1
 */
export type TooltipTone = 'primary' | 'amber' | 'green' | 'red';

const TONE: Record<TooltipTone, { bg: string; fg: string; subtitle: string }> = {
  primary: { bg: 'bg-primary-600', fg: 'text-white', subtitle: 'text-white/80' },
  amber: { bg: 'bg-amber-500', fg: 'text-gray-900', subtitle: 'text-gray-900/70' },
  green: { bg: 'bg-emerald-700', fg: 'text-white', subtitle: 'text-white/80' },
  red: { bg: 'bg-red-600', fg: 'text-white', subtitle: 'text-white/80' },
};

/**
 * How close the arrow may get to a corner of the bubble.
 *
 * The bubble's radius plus the arrow's own half-width: any closer and the
 * arrow's tip pokes out of the curve rather than the flat edge, which reads as
 * a rendering fault rather than as a pointer.
 */
const ARROW_INSET = 12;

interface TooltipProps {
  children: ReactNode;
  /** Main tooltip text */
  title: string;
  /** Optional subtitle (smaller, lighter text) */
  subtitle?: string;
  /** Delay in ms before showing tooltip (default: 0) */
  delay?: number;
  /** Position of tooltip relative to children (default: 'top')
   * 'vertical' will prefer bottom but flip to top if not enough space */
  position?: 'top' | 'bottom' | 'left' | 'right' | 'vertical';
  /** Max width of tooltip in pixels (enables text wrapping) */
  maxWidth?: number;
  /** When true, tooltip matches the width of its trigger element */
  matchWidth?: boolean;
  /** Additional class names for the wrapper element */
  className?: string;
  /** When true, renders children without the tooltip wrapper */
  disabled?: boolean;
  /** Colour family, matching the control described. Defaults to primary. */
  tone?: TooltipTone;
  /**
   * Hide on click, and stay hidden until the pointer leaves and returns.
   *
   * For a tooltip on a button that ACTS. After the click the label usually
   * describes something that already happened, or flips to its opposite —
   * "Pause" becoming "Resume" under a stationary cursor — and a bubble that
   * rewrites itself in place reads as a glitch rather than as feedback. The
   * pointer leaving and coming back is the signal that the reader is asking
   * again.
   */
  hideOnClick?: boolean;
}

export function Tooltip({
  children,
  title,
  subtitle,
  delay = 0,
  position = 'top',
  maxWidth,
  matchWidth = false,
  className = '',
  disabled = false,
  tone = 'primary',
  hideOnClick = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  // Initialize with position absolute and off-screen to prevent width expansion
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({
    position: 'absolute',
    top: -9999,
    left: -9999,
  });
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({});
  const [resolvedPosition, setResolvedPosition] = useState<'top' | 'bottom' | 'left' | 'right'>(
    position === 'vertical' ? 'bottom' : position
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current?.offsetHeight || 40;
    const tooltipWidth = tooltipRef.current?.offsetWidth || 100;
    const gap = 8; // Space between trigger and tooltip

    let finalPosition = position === 'vertical' ? 'bottom' : position;

    // For vertical mode, check if there's enough space below
    if (position === 'vertical') {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow < tooltipHeight + gap + 16 && spaceAbove > spaceBelow) {
        finalPosition = 'top';
      } else {
        finalPosition = 'bottom';
      }
    }

    setResolvedPosition(finalPosition as 'top' | 'bottom' | 'left' | 'right');

    let top = 0;
    let left = 0;
    let arrowTop: string | number = '50%';
    let arrowLeft: string | number = '50%';
    let arrowTransform = 'rotate(45deg)';

    switch (finalPosition) {
      case 'top':
        top = rect.top - tooltipHeight - gap + window.scrollY;
        left = rect.left + rect.width / 2 - tooltipWidth / 2 + window.scrollX;
        arrowTop = 'auto';
        arrowLeft = '50%';
        arrowTransform = 'translateX(-50%) rotate(45deg)';
        break;
      case 'bottom':
        top = rect.bottom + gap + window.scrollY;
        left = rect.left + rect.width / 2 - tooltipWidth / 2 + window.scrollX;
        arrowTop = -4;
        arrowLeft = '50%';
        arrowTransform = 'translateX(-50%) rotate(45deg)';
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2 + window.scrollY;
        left = rect.left - tooltipWidth - gap + window.scrollX;
        arrowTop = '50%';
        arrowLeft = 'auto';
        arrowTransform = 'translateY(-50%) rotate(45deg)';
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2 + window.scrollY;
        left = rect.right + gap + window.scrollX;
        arrowTop = '50%';
        arrowLeft = -4;
        arrowTransform = 'translateY(-50%) rotate(45deg)';
        break;
    }

    // Prevent tooltip from going off-screen horizontally
    const padding = 8;
    if (left < padding) {
      left = padding;
    } else if (left + tooltipWidth > window.innerWidth - padding) {
      left = window.innerWidth - tooltipWidth - padding;
    }

    /**
     * Re-aim the arrow at the TRIGGER after that clamp.
     *
     * The bubble is centred on the trigger, so an arrow at 50% points at it —
     * right up until the clamp above slides the bubble back inside the viewport
     * and leaves the arrow pointing at empty space beside the control it
     * describes. A wide bubble on a small control near an edge is exactly when
     * that happens, and it is also when the reader most needs to know which of
     * several controls the bubble belongs to.
     *
     * Clamped to `ARROW_INSET` so it never rides up onto a rounded corner, and
     * a no-op when nothing was clamped: `triggerCentre - left` is then
     * `tooltipWidth / 2`, which is where it already was.
     */
    if (finalPosition === 'top' || finalPosition === 'bottom') {
      const triggerCentre = rect.left + rect.width / 2 + window.scrollX;
      arrowLeft = Math.max(
        ARROW_INSET,
        Math.min(tooltipWidth - ARROW_INSET, triggerCentre - left),
      );
    }

    setTooltipStyle({
      position: 'absolute',
      top,
      left,
    });

    setArrowStyle({
      position: 'absolute',
      top: arrowTop,
      left: arrowLeft,
      right: finalPosition === 'left' ? -4 : undefined,
      bottom: finalPosition === 'top' ? -4 : undefined,
      transform: arrowTransform,
    });
  };

  /**
   * What opened the last interaction. A tap synthesises `mouseenter`, so
   * without this a touch user gets a hover tooltip they have no gesture to
   * dismiss — it just sits there until they happen to tap something else.
   */
  const lastPointerTypeRef = useRef<string>('mouse');

  const handlePointerEnter = (e: React.PointerEvent) => {
    lastPointerTypeRef.current = e.pointerType || 'mouse';
  };

  /** Set by a click while `hideOnClick`; cleared when the pointer leaves. */
  const [suppressed, setSuppressed] = useState(false);

  /**
   * Hide BEFORE the control's own handler runs.
   *
   * Bound on the capture phase, and on pointer-down rather than click. On the
   * bubble phase this fired after the button had already acted, so the flip
   * rendered first — one frame of the tooltip still open with its new label,
   * repositioning — and only then did it hide. That frame was the flash.
   * Capturing the press instead means the hide is queued in the same batch as
   * the state change, so there is no in-between render to see.
   */
  const handleActivate = () => {
    if (!hideOnClick) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
    setSuppressed(true);
  };

  const handleMouseEnter = () => {
    // Touch gets no hover tooltip. Mouse and pen still do.
    if (lastPointerTypeRef.current === 'touch') return;
    if (suppressed) return;
    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, delay);
    } else {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Leaving is the reset: coming back is a fresh question, and deserves a
    // fresh answer.
    setSuppressed(false);
    setIsVisible(false);
  };

  // Keyboard focus handlers for WCAG 2.1.1 accessibility
  const handleFocus = (e: React.FocusEvent) => {
    // Tapping a button also focuses it, so this fired on touch too — the other
    // half of the stuck-tooltip bug, and the half `mouseenter` guards miss.
    // `:focus-visible` is exactly the "focused for a keyboard reason" signal,
    // so keyboard users keep the tooltip 2.1.1 requires and touch does not.
    const target = e.target as HTMLElement | null;
    if (target?.matches && !target.matches(':focus-visible')) return;
    // A keyboard activation leaves focus where it was, so the same staleness
    // applies; Tab away and back is the keyboard's "ask again".
    if (suppressed) return;
    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(true);
      }, delay);
    } else {
      setIsVisible(true);
    }
  };

  const handleBlur = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setSuppressed(false);
    setIsVisible(false);
  };

  // Dismiss tooltip on Escape key (WCAG 1.4.13)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        setIsVisible(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isVisible]);

  // Update position when visible (handles initial render and content changes)
  // Use double requestAnimationFrame to ensure DOM is fully painted before measuring
  //
  // `title`/`subtitle` are in the deps because the CONTENT half of that promise
  // was not being kept: with only `[isVisible]`, a label that changes while the
  // tooltip is open kept the left offset computed for the OLD text. The bubble
  // is centred on its trigger, so a toggle whose label changes width — Pause to
  // Resume, say — visibly jumped sideways by half the difference.
  useEffect(() => {
    if (isVisible) {
      requestAnimationFrame(() => {
        requestAnimationFrame(updatePosition);
      });
    }
  }, [isVisible, title, subtitle]);

  // Update resolved position when position prop changes
  useEffect(() => {
    if (position !== 'vertical') {
      setResolvedPosition(position);
    }
  }, [position]);

  /**
   * Above every other layer, deliberately.
   *
   * A tooltip only exists while something is hovered, and that something is by
   * definition already on top — so anything that can cover the trigger must not
   * cover its explanation. At `z-[9999]` it TIED with popovers (which set that
   * inline) and lost on DOM order, so the tooltip on a control inside a popover
   * rendered behind it and could not be read at all.
   *
   * `10050` clears the `10000` band the full-screen takeovers use
   * (DownloadAppModal, RenewalIframe, point notifications, the demo goals sheet).
   */
  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={`px-3 py-2 ${TONE[tone].bg} ${TONE[tone].fg} text-sm rounded-md shadow-lg pointer-events-none z-[10050] transition-opacity duration-200 text-center ${
        maxWidth || matchWidth ? 'whitespace-normal' : 'whitespace-nowrap'
      } ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      style={{
        ...tooltipStyle,
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
        width: matchWidth && containerRef.current ? `${containerRef.current.getBoundingClientRect().width}px` : undefined,
      }}
    >
      {title}
      {subtitle && (
        <div className={`${TONE[tone].subtitle} text-xs mt-0.5`}>{subtitle}</div>
      )}
      <div
        className={`absolute w-2 h-2 ${TONE[tone].bg}`}
        style={arrowStyle}
      />
    </div>
  );

  // When disabled, render children without any tooltip wrapper
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <span
      ref={containerRef}
      className={`relative inline-flex ${className || 'w-fit'}`}
      onPointerEnter={handlePointerEnter}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDownCapture={handleActivate}
      // Keyboard activation raises `click` with no pointer event before it, so
      // both are needed. `handleActivate` is idempotent.
      onClickCapture={handleActivate}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {children}
      {/*
        Unmounted while suppressed, not merely faded.
        Hiding it set `opacity-0`, but the bubble carries a 200ms transition —
        so it was still on screen, mid-fade, when the click's state change
        landed and rewrote its label. What flashed was the OLD bubble wearing
        the NEW text. There is no fade to catch if it is not rendered.
      */}
      {typeof window !== 'undefined' &&
        !suppressed &&
        createPortal(tooltipContent, document.body)}
    </span>
  );
}
